import { inject, Injectable, Signal } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { UserRoleFilterDialogComponent } from '@app/components/dialogs/select-user-role-dialog/select-user-role-dialog.component';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { ProjectTaskUserRole } from '@app/shared/types/enums/project-task-user-role.enum';
import { TaskFilters, TaskUserRoleFilter } from '@app/shared/types/models/task/task-filters.model';
import { User } from '@app/shared/types/models/user/user.model';
import { TranslateService } from '@ngx-translate/core';
import { TaskFiltersService } from './task-filters.service';

/**
 * TaskFiltersHelperService - Presentation helpers for task list filters (modals, labels, display strings).
 *
 * Does not expose filter state or options; reads {@link TaskFiltersService} only for internal use.
 * Components must inject {@link TaskFiltersService} to read or mutate filter state after modal results.
 *
 * Scope: Route-level instance via `providers` on the project tasks view (same injector as {@link TaskFiltersService}).
 *
 * Usage: Toolbar templates for user-role picker entry points, date display parsing, and tooltip text.
 *
 * Architecture:
 * - {@link TaskFiltersService}: Authoritative state, URL sync, available options
 * - This service: Translate-backed labels, modal opening (streams), derived display strings
 */
@Injectable()
export class TaskFiltersHelperService {
  private readonly taskFiltersState: TaskFiltersService = inject(TaskFiltersService);
  private readonly dialogService: DialogService = inject(DialogService);
  private readonly translateService: TranslateService = inject(TranslateService);

  private readonly filters: Signal<TaskFilters | null> = this.taskFiltersState.filters;
  private readonly availableUsers: Signal<User[]> = this.taskFiltersState.availableUsers;
  private readonly availableGroupAdmins: Signal<User[]> = this.taskFiltersState.availableGroupAdmins;
  private readonly availableIndustryAdmins: Signal<User[]> = this.taskFiltersState.availableIndustryAdmins;

  public openSelectUserRoleDialog(): MatDialogRef<UserRoleFilterDialogComponent, TaskUserRoleFilter[]> {
    const selectedUserRole = this.filters()?.userRole ?? [];
    return this.dialogService.openSelectUserRoleDialog({
      availableUsers: this.availableUsers(),
      availableGroupAdmins: this.availableGroupAdmins(),
      availableIndustryAdmins: this.availableIndustryAdmins(),
      selectedUserRole
    });
  }

  public userRoleDisplayValue(): string {
    const selectedUserRole = this.filters()?.userRole ?? [];
    if (!selectedUserRole.length) return '';
    const userById = new Map<string, User>(
      [...this.availableUsers(), ...this.availableGroupAdmins(), ...this.availableIndustryAdmins()].map(user => [
        user.id,
        user
      ])
    );
    const roleLabelKey = (role: ProjectTaskUserRole): string => {
      switch (role) {
        case ProjectTaskUserRole.USER:
          return 'task-filters.user-role-user';
        case ProjectTaskUserRole.INDUSTRY_ADMIN:
          return 'task-filters.user-role-industry-admin';
        case ProjectTaskUserRole.GROUP_ADMIN:
          return 'task-filters.user-role-group-admin';
        default:
          return role;
      }
    };
    return selectedUserRole
      .filter(
        userRoleFilter => userRoleFilter.applicationUserId !== null && userRoleFilter.applicationUserId !== undefined
      )
      .map(
        userRoleFilter =>
          `${userById.get(userRoleFilter.applicationUserId)?.fullName ?? userRoleFilter.applicationUserId} (${this.translateService.instant(roleLabelKey(userRoleFilter.role))})`
      )
      .join(', ');
  }

  public parseDateFromFilter(dateString: string | undefined): Date | null {
    if (!dateString) return null;
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  }

  public isDateRangeFilterActive(startDateFrom: string | undefined, startDateTo: string | undefined): boolean {
    return (
      (startDateFrom !== null && startDateFrom !== undefined) || (startDateTo !== null && startDateTo !== undefined)
    );
  }
}
